import React, {useMemo, useState} from 'react';
import {Table, Box, Header, Toggle} from '@amzn/awsui-components-react';
import {useCollection} from '@amzn/awsui-collection-hooks';

export default function BoscoExplainerTable({data, finalColumns, postProcessColumns, modelComponentColumns, friendlyNameMapping}) {
    const [showPercentages, setShowPercentages] = useState<boolean>(false);
    const uniqueDates: any[] = useMemo(() => {
        return [...new Set(data.map((item) => item.balanceDate))].sort();
    }, [data]);

    const tableData: any[] = useMemo(
        () => transformData(data, uniqueDates, finalColumns, modelComponentColumns, postProcessColumns),
        [data, uniqueDates, finalColumns, modelComponentColumns, postProcessColumns]
    );

    const {items: rootItems, collectionProps} = useCollection(tableData, {
        expandableRows: {
            getId: (item) => item.metric,
            getParentId: (item) => item.parentMetric,
        },
    });

    function formatDateWithDayOfWeek(dateString: string): string {
        // Parse the date string manually to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        const dayOfWeek = date.toLocaleDateString('en-US', {weekday: 'short'});
        return `${dateString} (${dayOfWeek})`;
    }

    function getMetricValue(item: any, date: string) {
        const value = item[date].value;
        const isNumber = typeof value === 'number';
        const formattedValue = isNumber ? value.toFixed(2) : value;

        // Check if this is raw_forecast or final_forecast
        const isForecastMetric = ['cd_mean', 'raw_forecast', 'final_forecast'].includes(item.metric);

        // Calculate percentage relative to cd_mean
        let percentageText = '';
        if (item[date].percentage) {
            // Find the raw_forecast value for this date
            const percentage = item[date].percentage * 100;
            percentageText = ` (${percentage.toFixed(2)}%)`;
        }

        if (isForecastMetric) {
            return <b>{formattedValue}</b>;
        }

        // For other metrics, apply color coding
        if (isNumber) {
            const visuallyFormattedValue = value >= 0 ? `+${formattedValue}` : formattedValue;

            return (
                <span style={{color: value >= 0 ? 'green' : 'red'}}>
                    {visuallyFormattedValue}
                    {showPercentages ? percentageText : ''}
                </span>
            );
        }

        return formattedValue;
    }

    return (
        <Table
            data-testid="page-table"
            stickyHeader
            stripedRows
            stickyColumns={{first: 1}}
            columnDefinitions={[
                {
                    id: 'metric',
                    header: 'Metric',
                    isRowHeader: true,
                    cell: (item: any) => <b>{friendlyNameMapping[item.metric]}</b>,
                },
                ...uniqueDates.map((date) => ({
                    id: date,
                    header: formatDateWithDayOfWeek(date),
                    isRowHeader: true,
                    cell: (item: any) => getMetricValue(item, date),
                })),
            ]}
            header={
                <Header
                    actions={
                        <Toggle
                            data-testid="percentage-toggle"
                            onChange={({detail}) => setShowPercentages(detail.checked)}
                            checked={showPercentages}
                        >
                            Show % of Training CD Average
                        </Toggle>
                    }
                />
            }
            items={rootItems}
            {...collectionProps}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No matching data</b>
                    <Box variant="p" color="inherit">
                        There is no matching data to display
                    </Box>
                </Box>
            }
        ></Table>
    );
}

function transformData(
    data: any[],
    uniqueDates: string[],
    finalColumns: string[],
    modelComponentColumns: string[],
    postProcessColumns: string[]
) {
    if (!data.length || !finalColumns.length) return [];

    const aggData = data.map((record) => {
        return {
            ...record,
            post_process_contributions: postProcessColumns.reduce(
                (accumulator, currentValue) => accumulator + (record[currentValue] || 0),
                0
            ),
        };
    });

    // Create pivoted structure
    return [...finalColumns, ...postProcessColumns].map((column) => {
        const row: any = {metric: column};
        uniqueDates.forEach((date: any) => {
            const dataForDate = aggData.find((item) => item.balanceDate === date);
            row[date] = {
                value: dataForDate ? dataForDate[column] : 0,
                percentage: modelComponentColumns.includes(column)
                    ? dataForDate
                        ? dataForDate[column] / dataForDate['cd_mean']
                        : 0
                    : null,
            };
        });
        row.parentMetric = postProcessColumns.includes(column) ? 'post_process_contributions' : null;
        return row;
    });
}
